import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Globe, Plus, Trash, ArrowClockwise, ChatCircle } from "@phosphor-icons/react";
import axios from "axios";
import config from "../config";

const API = `${config.BACKEND_URL}/api`;

export default function WebsitesPage() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [websites, setWebsites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");

  const fetchWebsites = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/websites`, { headers: getAuthHeaders() });
      setWebsites(res.data.websites || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchWebsites();
    const interval = setInterval(fetchWebsites, 5000);
    return () => clearInterval(interval);
  }, [fetchWebsites]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setAdding(true);
    setError("");
    try {
      await axios.post(
        `${API}/websites`,
        { url: url.trim(), name: name.trim() || null },
        { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
      );
      setUrl("");
      setName("");
      setDialogOpen(false);
      fetchWebsites();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to add website");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this website and all its data?")) return;
    try {
      await axios.delete(`${API}/websites/${id}`, { headers: getAuthHeaders() });
      setWebsites((prev) => prev.filter((w) => w.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  const handleRescrape = async (id) => {
    try {
      await axios.post(`${API}/websites/${id}/rescrape`, {}, { headers: getAuthHeaders() });
      fetchWebsites();
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-sm font-mono uppercase tracking-[0.2em] text-[#6B7280]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="websites-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Websites</h1>
          <p className="text-sm text-[#6B7280] mt-1">Manage your scraped data sources.</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="rounded-none bg-[#002FA7] hover:bg-[#001F73] text-white text-sm h-9 transition-transform active:scale-[0.98]"
              data-testid="add-website-button"
            >
              <Plus size={16} weight="bold" className="mr-2" />
              Add Website
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none border-[#E5E7EB] max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold">Add Website</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAdd} className="space-y-5 mt-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3" data-testid="add-website-error">
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A]/60">
                  Website URL
                </Label>
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com"
                  required
                  className="rounded-none border-[#D1D5DB]"
                  data-testid="add-website-url-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A]/60">
                  Display Name (optional)
                </Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="My Website"
                  className="rounded-none border-[#D1D5DB]"
                  data-testid="add-website-name-input"
                />
              </div>
              <Button
                type="submit"
                disabled={adding}
                className="w-full rounded-none bg-[#002FA7] hover:bg-[#001F73] text-white h-11"
                data-testid="add-website-submit-button"
              >
                {adding ? "Adding..." : "Add & Start Scraping"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Website List */}
      {websites.length === 0 ? (
        <div className="border border-dashed border-[#D1D5DB] p-16 text-center" data-testid="websites-empty">
          <Globe size={48} weight="thin" className="mx-auto text-[#D1D5DB] mb-4" />
          <p className="text-sm text-[#6B7280] mb-2">No websites yet.</p>
          <p className="text-xs text-[#6B7280]/60">Click "Add Website" to scrape your first site.</p>
        </div>
      ) : (
        <div className="border border-[#E5E7EB]">
          <div className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB]">
            <div className="col-span-5 text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A]/40">Website</div>
            <div className="col-span-2 text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A]/40">Status</div>
            <div className="col-span-2 text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A]/40">Chunks</div>
            <div className="col-span-3 text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A]/40 text-right">Actions</div>
          </div>

          {websites.map((w, i) => (
            <div
              key={w.id}
              className={`grid grid-cols-12 gap-4 px-4 py-4 items-center hover:bg-[#F9FAFB] transition-colors ${
                i < websites.length - 1 ? "border-b border-[#E5E7EB]" : ""
              }`}
              data-testid={`website-item-${i}`}
            >
              <div className="col-span-5 flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 border border-[#E5E7EB] flex items-center justify-center flex-shrink-0">
                  <Globe size={18} weight="bold" className="text-[#6B7280]" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{w.name || w.url}</div>
                  <div className="text-xs text-[#6B7280] truncate">{w.url}</div>
                </div>
              </div>
              <div className="col-span-2">
                <span
                  className={`text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-1 inline-block ${
                    w.status === "ready"
                      ? "bg-emerald-50 text-emerald-700"
                      : w.status === "error"
                      ? "bg-red-50 text-red-700"
                      : w.status === "scraping"
                      ? "bg-blue-50 text-blue-700"
                      : "bg-amber-50 text-amber-700"
                  }`}
                >
                  {w.status}
                </span>
              </div>
              <div className="col-span-2 text-sm text-[#6B7280] font-mono">
                {w.chunk_count || 0}
              </div>
              <div className="col-span-3 flex items-center justify-end gap-2">
                {w.status === "ready" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/chatbot/${w.id}`)}
                    className="rounded-none border-[#D1D5DB] text-xs h-8"
                    data-testid={`configure-chatbot-${i}`}
                  >
                    <ChatCircle size={14} className="mr-1" />
                    Configure
                  </Button>
                )}
                <button
                  onClick={() => handleRescrape(w.id)}
                  className="p-2 text-[#6B7280] hover:text-[#002FA7] hover:bg-[#F3F4F6] transition-colors"
                  title="Re-scrape"
                  data-testid={`rescrape-website-${i}`}
                >
                  <ArrowClockwise size={16} weight="bold" />
                </button>
                <button
                  onClick={() => handleDelete(w.id)}
                  className="p-2 text-[#6B7280] hover:text-red-600 hover:bg-red-50 transition-colors"
                  title="Delete"
                  data-testid={`delete-website-${i}`}
                >
                  <Trash size={16} weight="bold" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
